import firebase from 'firebase';
import { AppDispatch } from '../../store';
import { resetAvailabilities, updateAvailabilitySlots } from './availability.slice';
import { DateTime } from 'luxon';
import * as cron from 'cron-parser';
import { Restaurant } from '../../models/restaurant';
import { Menu } from '../../models/menu';
import { FirestoreHelper } from '../../helpers/firestoreHelper';
import { serializeAvailability } from './availability.service';

let unsubscribeAvailability: firebase.Unsubscribe | null = null;
let activeAvailabilityKey: string | null = null;

const MAX_ITERATIONS = 1000;

export const syncAvailabilityListener = (
  restaurant: Restaurant | null,
  menu: Menu | null,
  dispatch: AppDispatch
) => {
  const key = restaurant && menu ? `${restaurant.id}::${menu.docId}` : null;
  if (key === activeAvailabilityKey) return;

  if (unsubscribeAvailability) { unsubscribeAvailability(); unsubscribeAvailability = null; }

  activeAvailabilityKey = key;

  if (!key || !restaurant || !menu) {
    dispatch(resetAvailabilities());
    return;
  }

  try {
    const timezoneOffset = new Date().getTimezoneOffset() / 60;
    const defaultMinOffset = restaurant.take_away_delivery_interval ?? 30;
    const daysToShowSlots = restaurant.days_show_slots_takeaway_delivery ?? 2;

    const dateTimeStart = DateTime.local().set({ second: 0 }).plus({ minutes: defaultMinOffset }).toJSDate();
    const dateTimeEnd = DateTime.local().set({ second: 0 }).plus({ days: daysToShowSlots }).endOf('day').toJSDate();

    unsubscribeAvailability = FirestoreHelper
      .getTakeAwaySlots(
        restaurant.id,
        firebase.firestore.Timestamp.fromDate(dateTimeStart),
        firebase.firestore.Timestamp.fromDate(dateTimeEnd)
      )
      .onSnapshot(
        (snapshot) => {
          if (!snapshot) return;

          const availabilities = menu.take_away_delivery_availability ?? [];
          const takeAwayInterval = restaurant.take_away_delivery_interval ?? menu.take_away_delivery_interval ?? 60;
          const maxSlots = restaurant.max_take_away_delivery_slots ?? { max_take_away: 0, max_delivery: 0 };

          const actualTime = new Date(Date.now() - 1000 * takeAwayInterval);
          const options = { currentDate: actualTime, endDate: dateTimeEnd, tz: 'Europe/Rome' };

          const evaluatedAvailabilities: Date[] = [];
          availabilities.forEach((availabilityStr: string) => {
            let count = 0;
            const interval = cron.parseExpression(availabilityStr, options);
            while (interval.hasNext() && count < MAX_ITERATIONS) {
              const next = interval.next().toDate();
              if (next.getTime() > dateTimeStart.getTime()) evaluatedAvailabilities.push(next);
              count++;
            }
          });

          const sorted = Array.from(new Set(evaluatedAvailabilities)).sort((a, b) => a.getTime() - b.getTime());
          dispatch(updateAvailabilitySlots({
            availabilities: sorted.map((av) =>
              serializeAvailability(av, takeAwayInterval, timezoneOffset, snapshot, maxSlots.max_take_away, maxSlots.max_delivery)
            ),
          }));
        },
        (error) => {
          console.error('Availability listener error:', error);
          activeAvailabilityKey = null;
          unsubscribeAvailability = null;
        }
      );
  } catch (e) {
    console.error('Failed to start availability listener:', e);
    activeAvailabilityKey = null;
  }
};